import React, { useCallback, useEffect, useMemo, useState } from "react";
import Container from "@mui/material/Container";
import { Lang, ProjectInterface } from "./types";
import { PROJECT_LIST } from "./constants";
import { Grid } from "@mui/material";
import { useChatBotContext } from "./context";
import { theme } from "../../styles/theme";
import { Navbar } from "../../components/navbar";
import { Header } from "../../components/header";
import { t } from "../../i18n/translates";
import { ThemeProvider } from "@mui/material/styles";
import { PromptEditor } from "./components/PromptEditor";
import { getBotSettings } from "./api";
import { ChatContainer } from "./components/Chat";
import { useAppDispatch } from "../../redux/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import VadSettings from "./components/VadOptions";
import { createErrorHandler } from "./errorHandler";
import { useVoiceChatSession } from "./hooks/useVoiceChatSession";
import { useChatMessageHandler } from "./hooks/useChatMessageHandler";
import { useConfigUpdater } from "./hooks/useConfigUpdater";
import { VoiceControls } from "./components/VoiceControls";
import { ProjectLanguageSelector } from "./components/ProjectLanguageSelector";
import { ChatInput } from "./components/ChatInput";

export function VoiceBot() {
    const { search } = useLocation();
    const selectedProjectKey = new URLSearchParams(search).get("project") || localStorage.getItem("_project");
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [selectedProject, selectProject] = useState<ProjectInterface>(
        PROJECT_LIST.find((p) => p.key === selectedProjectKey) || PROJECT_LIST[0]
    );

    const [options, setOptions] = useChatBotContext();

    // Error handler
    const errorHandler = useMemo(() => createErrorHandler(dispatch), [dispatch]);

    // Voice chat session management
    const { voiceChat, isStarted, start, stop, handleAddChat } = useVoiceChatSession({
        versionEndpoint: selectedProject.versionEndpoint,
        options,
    });

    // Chat message handling
    const { isLoading, newMessageText, setNewMessageText, handleSendNewMessage, handleRegenerate, selectedChat } =
        useChatMessageHandler({
            versionEndpoint: selectedProject.versionEndpoint,
            lang: options.lang,
            errorHandler,
        });

    // Config updater
    const { handleApplyPrompt, handleApplyGreeting, handleApplyTtsInstructions } = useConfigUpdater({
        selectedProject,
        voiceChat,
        setOptions,
        errorHandler,
        options,
    });

    // Project selection handler
    const setSelectProject = useCallback(
        (p: ProjectInterface) => {
            selectProject(p);
            setOptions({
                lang: p.defaultLang,
                isRealtime: !!p.isRealtime,
            });

            navigate(`?project=${p.key}`, {
                replace: true,
            });

            localStorage.setItem("_project", p.key);
        },
        [navigate, setOptions]
    );

    // Prompt change handler
    const onPromptChange = useCallback(
        (newPrompt: string) => {
            setOptions({
                instructions: newPrompt,
            });
        },
        [setOptions]
    );

    // Language change handler
    const handleLanguageChange = useCallback(
        (lang: Lang) => {
            setOptions({ lang });
        },
        [setOptions]
    );

    // Load bot settings on mount or when project/language changes
    useEffect(() => {
        getBotSettings(selectedProject.versionEndpoint, options.lang, selectedProject.isRealtime)
            .then(({ prompt, greeting, ttsVoice, ttsSpeed, ttsInstructions }) => {
                console.log("Load Bot Settings", { prompt, greeting, ttsVoice, ttsSpeed, ttsInstructions });

                setOptions({
                    defaultInstruction: prompt,
                    instructions: prompt,
                    greeting,
                    voice: ttsVoice || "",
                    speed: ttsSpeed || 1,
                    ttsInstructions: ttsInstructions || "",
                });
            })
            .catch((error) => errorHandler.handle(error));
    }, [selectedProject.isRealtime, selectedProject.versionEndpoint, options.lang, setOptions, errorHandler]);

    return (
        <ThemeProvider theme={theme}>
            <Navbar />
            <Header
                title={t("nav.voice_bot")}
                actions={[
                    <ProjectLanguageSelector
                        key="project-language-selector"
                        selectedProject={selectedProject}
                        onProjectChange={setSelectProject}
                        lang={options.lang}
                        onLangChange={handleLanguageChange}
                        isStarted={isStarted}
                    />,
                ]}
            />
            <Container maxWidth={false} sx={{ mt: 2, mb: 2, height: "calc(100vh - 150px)" }}>
                <Grid container spacing={2} style={{ height: "100%" }}>
                    <Grid item md={6}>
                        <PromptEditor
                            isStarted={isStarted}
                            project={selectedProject}
                            options={options}
                            catchError={(error) => errorHandler.handle(error)}
                            onPromptChange={onPromptChange}
                            applyPrompt={handleApplyPrompt}
                            applyGreeting={handleApplyGreeting}
                            applyTtsInstructions={handleApplyTtsInstructions}
                        />
                    </Grid>
                    <Grid
                        item
                        md={6}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <Grid
                            container
                            spacing={1}
                            sx={{
                                justifyContent: "space-between",
                                mb: 1,
                            }}
                        >
                            <Grid item>
                                <VadSettings
                                    options={options}
                                    setOptions={setOptions}
                                    isDisabled={isStarted || !!selectedProject.isRealtime}
                                />
                            </Grid>
                            <Grid item>
                                <VoiceControls
                                    options={options}
                                    setOptions={setOptions}
                                    isStarted={isStarted}
                                    isLoading={isLoading}
                                    selectedProject={selectedProject}
                                    onStartStop={() => (isStarted ? stop() : start())}
                                />
                            </Grid>
                        </Grid>

                        <ChatContainer
                            chat={selectedChat}
                            handleRegenerate={selectedProject.isRealtime ? undefined : handleRegenerate}
                        />

                        <ChatInput
                            newMessageText={newMessageText}
                            onMessageChange={setNewMessageText}
                            onSendMessage={handleSendNewMessage}
                            onAddChat={handleAddChat}
                            isDisabled={isStarted || !!selectedProject.isRealtime}
                            isLoading={isLoading}
                        />
                    </Grid>
                </Grid>
            </Container>
        </ThemeProvider>
    );
}
